import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Clock, Share2, ExternalLink } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  deepAnalysis: string;
  category: string;
  imageUrl: string | null;
  biasScore: number;
  noticiabilityTotal: number;
  scoreJson: {
    impact: number;
    reach: number;
    duration: number;
  };
  sourceName: string;
  sourceUrl: string;
  publishedAt: string;
}

const ArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showRegistration, setShowRegistration] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const { data: articleData, isLoading, error } = useQuery({
    queryKey: ['article', id],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/news/${id}`);
      return response.data;
    },
    enabled: !!id
  });

  const getFreeReadsUsed = (): number => {
    const reads = localStorage.getItem('freeAnalysisReads');
    return reads ? parseInt(reads) : 0;
  };

  const incrementFreeReads = () => {
    const current = getFreeReadsUsed();
    localStorage.setItem('freeAnalysisReads', String(current + 1));
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      localStorage.setItem('userEmail', userEmail);
      try {
        await axios.post('/api/v1/auth/subscribe', { email: userEmail });
      } catch (err) {}
      setShowRegistration(false);
      toast.success('¡Registro exitoso! Ya puedes leer todos los análisis.');
    } catch (error) {
      toast.error('Error en el registro. Inténtalo de nuevo.');
    }
  };

  if (error) {
    return (
      <div className="container-news py-12">
        <div className="text-center">
          <h2 className="font-playfair text-2xl font-bold text-primary-900 mb-4">Article not found</h2>
          <p className="text-primary-600">The article you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-news py-12">
        <div className="max-w-4xl mx-auto">
          <div className="loading-skeleton h-8 mb-4"></div>
          <div className="loading-skeleton h-64 mb-6"></div>
          <div className="loading-skeleton h-4 mb-2"></div>
          <div className="loading-skeleton h-4 mb-2"></div>
          <div className="loading-skeleton h-4 w-3/4"></div>
        </div>
      </div>
    );
  }

  const article: NewsArticle = articleData?.data;
  if (!article) return <div>Article not found</div>;

  const radarData = [
    { subject: 'Impact', value: article.scoreJson.impact, fullMark: 10 },
    { subject: 'Reach', value: article.scoreJson.reach, fullMark: 10 },
    { subject: 'Duration', value: article.scoreJson.duration, fullMark: 10 }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isRegistered = !!localStorage.getItem('userEmail');
  const freeReadsUsed = getFreeReadsUsed();

  // Check if deep analysis needs registration
  const needsRegistration = !isRegistered && freeReadsUsed >= 1;

  // Render markdown-like content with headers
  const renderAnalysis = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="font-playfair text-2xl font-bold text-primary-900 mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="font-semibold text-lg text-primary-800 mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-4 mb-2 text-primary-700">{line.replace('- ', '')}</li>;
      }
      if (line.match(/^\d+\.\s/)) {
        return <li key={i} className="ml-4 mb-2 text-primary-700 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-2"></div>;
      }
      // Handle bold text
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={i} className="text-primary-700 leading-relaxed mb-2">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="text-primary-900">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="bg-white">
      <article className="container-news py-12">
        <div className="max-w-4xl mx-auto">
          {/* Meta Info */}
          <div className="flex items-center justify-between mb-6 text-sm text-primary-600">
            <div className="flex items-center space-x-4">
              <span className="category-badge">{article.category}</span>
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {formatDate(article.publishedAt)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button className="flex items-center space-x-1 text-primary-600 hover:text-primary-900">
                <Share2 className="h-4 w-4" /><span>Share</span>
              </button>
            </div>
          </div>

          {/* Title */}
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-primary-900 leading-tight mb-6">
            {article.title}
          </h1>

          {/* Featured Image */}
          {article.imageUrl && (
            <div className="mb-8">
              <img src={article.imageUrl} alt={article.title} className="w-full h-64 md:h-80 object-cover newspaper-border" />
            </div>
          )}

          {/* Neutral Summary — our balanced summary */}
          <div className="bg-primary-50 newspaper-border p-6 mb-8">
            <h2 className="font-playfair text-xl font-bold text-primary-900 mb-4">
              Neutral Summary
            </h2>
            <p className="text-lg text-primary-800 leading-relaxed">
              {article.summary}
            </p>
            <p className="text-xs text-primary-400 mt-4 italic">
              Synthesized from multiple international sources by Unbiased News AI.
            </p>
          </div>

          {/* Deep Analysis — THE MAIN CONTENT */}
          {needsRegistration ? (
            <div className="newspaper-border bg-accent-50 p-8 mb-8 text-center">
              <h3 className="font-playfair text-2xl font-bold text-primary-900 mb-4">
                Deep Analysis
              </h3>
              <p className="text-primary-700 mb-6">
                Register to read our in-depth analysis with progressive vs conservative framing, blind spots in media coverage, and critical questions.
              </p>
              <button onClick={() => setShowRegistration(true)} className="btn-accent text-lg px-8 py-4">
                Register to Read — Free
              </button>
            </div>
          ) : (
            <div className="mb-8">
              {/* Track free read */}
              {!isRegistered && (() => { incrementFreeReads(); return null; })()}

              <div className="prose prose-lg max-w-none">
                {renderAnalysis(article.deepAnalysis)}
              </div>

              {/* Sources attribution */}
              <div className="mt-8 pt-4 border-t border-primary-200">
                <p className="text-sm text-primary-500">
                  <strong>Sources consulted:</strong> {article.sourceName}
                  {article.sourceUrl && (
                    <> — <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:underline inline-flex items-center">
                      Read original source <ExternalLink className="h-3 w-3 ml-1" />
                    </a></>
                  )}
                </p>
                <p className="text-xs text-primary-400 mt-1 italic">
                  This analysis is original content by Unbiased News, not a reproduction of any source material.
                </p>
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Sidebar metrics */}
      <section className="bg-primary-50 newspaper-divider">
        <div className="container-news py-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="headline-section mb-8">Story Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* Radar Chart */}
              <div className="bg-white newspaper-border p-6">
                <h4 className="font-playfair text-lg font-bold text-primary-900 mb-4">Impact Analysis</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} />
                    <Radar name="Score" dataKey="value" stroke="#f59e55" fill="#f59e55" fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-primary-600">Impact:</span>
                    <span className="font-bold">{article.scoreJson.impact}/10</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Global Reach:</span>
                    <span className="font-bold">{article.scoreJson.reach}/10</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Duration:</span>
                    <span className="font-bold">{article.scoreJson.duration}/10</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-primary-900">Total:</span>
                      <span className="font-bold text-lg text-accent-600">{article.noticiabilityTotal.toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sources & Category */}
              <div className="bg-white newspaper-border p-6">
                <h4 className="font-playfair text-lg font-bold text-primary-900 mb-4">Sources Consulted</h4>
                <div className="space-y-3">
                  {article.sourceName?.split(' | ').map((source: string, idx: number) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                      <span className="text-sm text-primary-700">{source}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 mt-4">
                  <div className="text-sm text-primary-600">
                    <span className="font-semibold">Category:</span> {article.category}
                  </div>
                </div>
              </div>

              {/* Story context */}
              <div className="bg-white newspaper-border p-6">
                <h4 className="font-playfair text-lg font-bold text-primary-900 mb-4">About This Analysis</h4>
                <div className="space-y-3 text-sm text-primary-700">
                  <p>
                    This article is an <strong>original analysis</strong> by Unbiased News, not a copy of any single source.
                  </p>
                  <p>
                    We review coverage from multiple international outlets across the political spectrum and synthesize a balanced perspective.
                  </p>
                  <p>
                    Our AI identifies how progressive and conservative media frame each story differently, so you can see the full picture.
                  </p>
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs text-primary-400">
                      Category: {article.category}<br/>
                      Sources: {article.sourceName}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistration && (
        <div className="modal-overlay" onClick={() => setShowRegistration(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">
              Register to continue reading
            </h3>
            <p className="text-primary-700 mb-6">
              Sign up to read all our in-depth analyses and receive a daily news summary in your email.
            </p>
            <form onSubmit={handleRegistration} className="space-y-4">
              <input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder="Enter your email" className="form-input" required />
              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1">Register</button>
                <button type="button" onClick={() => setShowRegistration(false)} className="btn-secondary">Cancel</button>
              </div>
              <p className="text-xs text-primary-500 text-center">We respect your privacy. Unsubscribe at any time.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlePage;
